import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import { CheckCircle, XCircle, UploadSimple } from '@phosphor-icons/react';

const tl = (v) => (v === null || v === undefined ? '—' : formatCurrency(v));
const fmt = (c) => (/_qty/.test(c.name) ? (v) => (v ?? '—') : tl);
const errText = (e) => e.response?.data?.detail || e.message || 'İşlem başarısız';

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-border/50 text-sm" data-testid={testid}>
    <span className="text-muted-foreground">{label}</span><span className="font-data text-right">{value}</span>
  </div>
);

const SimpleTable = ({ rows, testid, title }) => (
  <div data-testid={testid}>
    <p className="text-sm font-medium mb-1">{title}</p>
    <Table className="text-sm">
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}><TableCell>{r.label}</TableCell><TableCell className="text-right">{r.count ?? ''}</TableCell><TableCell className="text-right">{tl(r.amount)}</TableCell></TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const ChecksList = ({ checks }) => (
  <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="pos-checks">
    {checks.map((c) => (
      <div key={c.name} className="flex items-start gap-2 text-sm">
        {c.ok ? <CheckCircle className="text-emerald-600 shrink-0 mt-0.5" size={16} /> : <XCircle className="text-red-600 shrink-0 mt-0.5" size={16} />}
        <span className="break-words">{c.label}: beklenen {fmt(c)(c.expected)}, okunan {fmt(c)(c.actual)}</span>
      </div>
    ))}
  </div>
);

const DailyPreview = ({ p }) => {
  const s = p.summary;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <Row label="Menü kalemi satışı (brüt)" value={tl(s.menu_item_sales)} testid="pos-menu-item-sales" />
        <Row label="Toplam indirim" value={tl(s.total_discount)} testid="pos-total-discount" />
        <Row label="Net satış" value={tl(s.net_sales)} testid="pos-net-sales" />
        <Row label="Açılan / kapanan hesap" value={`${s.opened_checks ?? '—'} / ${s.closed_checks ?? '—'}`} />
        <Row label="Kuver" value={s.covers ?? '—'} />
        <Row label="Kuver ort. / Hesap ort." value={`${tl(s.avg_per_cover)} / ${tl(s.avg_per_check)}`} />
        <Row label="POS harcamaları (giderlere eklenmez)" value={tl(s.pos_expenses_total)} />
        <p className="text-xs text-muted-foreground mt-2">{s.period_note}. POS net satışı ERP günlük cirosuna eklenmez.</p>
      </div>
      <div className="space-y-4">
        <SimpleTable title="Ödemeler (ödeme yöntemi)" rows={p.sections.payments} testid="pos-payments" />
        <SimpleTable title="Sipariş tipleri (sipariş kanalı)" rows={p.sections.order_types} testid="pos-order-types" />
        <SimpleTable title="İşlem periyotları (2 saatlik)" rows={p.sections.periods} testid="pos-periods" />
        <SimpleTable title="İndirimler" rows={p.sections.discounts} testid="pos-discounts" />
        <SimpleTable title="İptaller / ödeme düzeltme" rows={[...p.sections.cancellations, ...p.sections.closed_check_ops]} testid="pos-cancellations" />
        {p.sections.refunds?.length > 0 && <SimpleTable title="İadeler" rows={p.sections.refunds} testid="pos-refunds" />}
        <SimpleTable title="POS harcamaları" rows={p.sections.pos_expenses} testid="pos-expenses" />
      </div>
    </div>
  );
};

const MenuPreview = ({ p }) => {
  const s = p.summary;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <Row label="Genel toplam" value={tl(s.grand_total)} testid="pos-grand-total" />
        <Row label="Toplam adet" value={s.grand_qty} />
        <Row label="Satır" value={s.item_rows} />
        <Row label="İkram satırı / adet" value={`${s.complimentary_rows} / ${s.complimentary_qty}`} testid="pos-complimentary" />
        <Row label="Yarım / 1,5 porsiyon satırı" value={s.portion_variant_rows} testid="pos-portion-variants" />
      </div>
      <div className="max-h-96 overflow-auto border rounded-lg" data-testid="pos-items-table">
        <Table className="text-sm">
          <TableHeader><TableRow><TableHead>Kod</TableHead><TableHead>Ürün</TableHead><TableHead>Varyasyon</TableHead><TableHead className="hidden md:table-cell">Grup</TableHead><TableHead className="text-right">Adet</TableHead><TableHead className="text-right">Tutar</TableHead></TableRow></TableHeader>
          <TableBody>
            {p.items.map((it) => (
              <TableRow key={it.line_no}>
                <TableCell className="font-data">{it.product_code}</TableCell>
                <TableCell className="max-w-[200px] break-words">{it.product_name}</TableCell>
                <TableCell>
                  {it.is_complimentary && <Badge variant="outline" className="mr-1">İkram</Badge>}
                  {it.portion !== 1 && <Badge variant="outline" className="mr-1">x{String(it.portion).replace('.', ',')}</Badge>}
                  {it.is_addition && <Badge variant="outline">İlave</Badge>}
                </TableCell>
                <TableCell className="hidden md:table-cell text-xs">{it.main_group} / {it.group}</TableCell>
                <TableCell className="text-right">{it.quantity}</TableCell>
                <TableCell className="text-right">{tl(it.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const ConfirmBar = ({ p, onDone }) => {
  const [replace, setReplace] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const res = await api.post('/pos-reports/confirm', { preview_id: p.preview_id, replace_existing: replace, replace_confirmation: text });
      toast.success(`Kaydedildi: ${res.data.report_date}${res.data.replaced ? ' (eski rapor arşivlendi)' : ''}`);
      onDone();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };
  if (!p.valid) return <p className="text-sm text-red-600" data-testid="pos-invalid">Rapor okunamadı veya toplamlar tutmuyor; kaydedilemez.</p>;
  return (
    <div className="space-y-2">
      {p.existing && (
        <div className="p-3 border border-amber-400 bg-amber-50 rounded-lg text-sm space-y-2" data-testid="pos-existing-warning">
          <p>Bu tarih ve tür için kayıtlı rapor var ({p.existing.file_name}, {p.existing.confirmed_by_name}).{p.same_file_as_existing ? ' Yüklenen dosya kayıtlı dosyayla aynı.' : ''}</p>
          <label className="flex items-center gap-2"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="pos-replace-checkbox" /> Kayıtlı raporu bununla değiştir (eski rapor arşivlenir)</label>
          {replace && <Input placeholder="Onay için DEĞİŞTİR yazın" value={text} onChange={(e) => setText(e.target.value)} className="max-w-xs" data-testid="pos-replace-text" />}
        </div>
      )}
      <Button onClick={save} disabled={busy || (p.existing && (!replace || text.trim().toLocaleUpperCase('tr-TR') !== 'DEĞİŞTİR'))} data-testid="pos-confirm-btn">
        {busy ? 'Kaydediliyor...' : 'Onayla ve Kaydet'}
      </Button>
    </div>
  );
};

const SavedList = ({ items }) => (
  <div data-testid="pos-saved-list">
    <p className="text-sm font-medium mb-1">Kayıtlı POS raporları ({items.length})</p>
    {items.length === 0 ? <p className="text-sm text-muted-foreground">Henüz rapor yüklenmedi.</p> : (
      <div className="max-h-48 overflow-auto"><Table className="text-sm"><TableBody>
        {items.map((r) => (
          <TableRow key={r.id}><TableCell>{r.report_date}</TableCell><TableCell>{r.report_type === 'menu_items' ? 'Menü Kalemi' : 'Günlük Ciro'}</TableCell><TableCell className="text-right">{tl(r.report_type === 'menu_items' ? r.summary?.grand_total : r.summary?.net_sales)}</TableCell><TableCell className="text-xs hidden md:table-cell">{r.file_name}</TableCell></TableRow>
        ))}
      </TableBody></Table></div>
    )}
  </div>
);

export const PosReportUpload = () => {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('smart_analytics', 'create');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [inputKey, setInputKey] = useState(0);

  const loadSaved = async () => {
    try { setSaved((await api.get('/pos-reports')).data); } catch (e) { toast.error(`POS raporları yüklenemedi: ${errText(e)}`); }
  };
  useEffect(() => { loadSaved(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setPreview(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      setPreview((await api.post('/pos-reports/preview', fd, { timeout: 60000, headers: { 'Content-Type': 'multipart/form-data' } })).data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="pos-upload-card">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><UploadSimple size={18} /> Orion POS Rapor Yükle</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {canCreate ? (
          <div className="flex flex-wrap items-center gap-3">
            <Input key={inputKey} type="file" accept="application/pdf,.pdf" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} className="max-w-sm" data-testid="pos-file-input" />
            <span className="text-xs text-muted-foreground">Menü Kalemi Raporu veya Genel Satış Raporu (PDF). Onaylanmadan kayıt yapılmaz.</span>
          </div>
        ) : <p className="text-sm text-muted-foreground">Rapor yükleme yetkiniz yok.</p>}
        {busy && <p className="text-sm text-muted-foreground" data-testid="pos-reading">PDF okunuyor...</p>}
        {preview && (
          <div className="space-y-4 border rounded-lg p-4" data-testid="pos-preview">
            <div className="flex flex-wrap gap-2 items-center">
              <Badge data-testid="pos-preview-type">{preview.report_type_label || 'Tanınmayan rapor'}</Badge>
              <Badge variant="outline" data-testid="pos-preview-date">{preview.report_date || 'Tarih yok'}</Badge>
              <Badge variant={preview.valid ? 'outline' : 'destructive'} data-testid="pos-preview-valid">{preview.valid ? 'Kontroller geçti' : 'Kontrol hatası'}</Badge>
              <span className="text-xs text-muted-foreground break-all">{preview.file_name}</span>
            </div>
            {preview.errors?.length > 0 && (
              <div className="text-sm text-red-600 space-y-1" data-testid="pos-errors">{preview.errors.slice(0, 20).map((e) => <p key={e}>{e}</p>)}</div>
            )}
            {preview.checks?.length > 0 && <ChecksList checks={preview.checks} />}
            {preview.info?.map((i) => <p key={i} className="text-xs text-muted-foreground">{i}</p>)}
            {preview.report_type === 'daily_sales' && preview.summary && <DailyPreview p={preview} />}
            {preview.report_type === 'menu_items' && preview.summary && <MenuPreview p={preview} />}
            <ConfirmBar p={preview} onDone={() => { setPreview(null); setInputKey((k) => k + 1); loadSaved(); }} />
          </div>
        )}
        <SavedList items={saved} />
      </CardContent>
    </Card>
  );
};
